import { State } from './types.js'
import { errorMessageFromBody } from './utils/api-error-message.js'

const http = async (state: State, relativeUrl: string, options?: RequestInit) => {
    const res = await fetch(`/api${relativeUrl}`, options)

    if (!res.ok) {
        // Show login screen on 401 Unauthorized
        if (res.status === 401) {
            state.isAuthenticated = false
            state._onAuthChange?.()
            // Return a never-resolving promise to prevent further execution
            return new Promise<never>(() => {})
        }

        const body: unknown = res.headers.get('content-type')?.startsWith('application/json')
            ? await res.json().catch(() => null)
            : null
        const message = (body as { message?: string } | null)?.message

        // The message reaches the user as a toast (eat, url-shortener, playtime,
        // survey), so it is resolved in the UI language exactly like the portal
        // client does: known code → translated, else the server text.
        const errorMessage = errorMessageFromBody(body, res.status, state.lang)

        // TODO more specific error type?
        const error = new Error(errorMessage)

        if (message) {
            const extendedError = error as Error & { sentryError?: Error }
            extendedError.sentryError = new Error(message)
        }

        throw error
    }

    return res
}

export const httpGet = async <T>(state: State, relativeUrl: string): Promise<T> => {
    const res = await http(state, relativeUrl)
    return res.json() as T
}

export const httpSend = async (
    state: State,
    method: 'POST' | 'PUT',
    relativeUrl: string,
    body: unknown,
) =>
    http(state, relativeUrl, {
        method,
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify(body),
    })

export const httpDelete = async (state: State, relativeUrl: string): Promise<'OK'> => {
    await http(state, relativeUrl, {
        method: 'DELETE',
    })

    return 'OK'
}

export const httpUpload = async (
    state: State,
    relativeUrl: string,
    file: Blob,
): Promise<{ imageUrl: string }> => {
    const formData = new FormData()
    formData.append('image', file, 'signature.png')

    const res = await http(state, relativeUrl, {
        method: 'POST',
        body: formData,
    })

    return res.json() as Promise<{ imageUrl: string }>
}
